import { useState, useEffect, useRef, useCallback } from 'react'
import { CKEditor } from '@ckeditor/ckeditor5-react'
import {
  ClassicEditor,
  Autoformat,
  Base64UploadAdapter,
  Bold,
  Italic,
  Underline,
  Strikethrough,
  Subscript,
  Superscript,
  BlockQuote,
  Essentials,
  Heading,
  Image,
  ImageCaption,
  ImageInsert,
  ImageStyle,
  ImageToolbar,
  ImageUpload,
  ImageResize,
  Indent,
  IndentBlock,
  Link,
  List,
  ListProperties,
  Paragraph,
  PasteFromOffice,
  Table,
  TableToolbar,
  TableCellProperties,
  TableProperties,
  Undo,
  HorizontalLine,
  SpecialCharacters,
  SpecialCharactersEssentials,
  Highlight,
} from 'ckeditor5'
import 'ckeditor5/ckeditor5.css'
import { sanitizeHtml } from '@/utils/sanitize'

const TOOLBAR = [
  'heading', '|',
  'bold', 'italic', 'underline', 'strikethrough', '|',
  'subscript', 'superscript', '|',
  'link', 'insertImage', 'insertTable', 'blockQuote', 'horizontalLine', '|',
  'bulletedList', 'numberedList', 'outdent', 'indent', '|',
  'specialCharacters', 'highlight', '|',
  'undo', 'redo',
]

const PLUGINS = [
  Autoformat, Base64UploadAdapter,
  Bold, Italic, Underline, Strikethrough, Subscript, Superscript,
  BlockQuote, Essentials, Heading,
  Image, ImageCaption, ImageInsert, ImageStyle, ImageToolbar, ImageUpload, ImageResize,
  Indent, IndentBlock,
  Link, List, ListProperties,
  Paragraph, PasteFromOffice,
  Table, TableToolbar, TableCellProperties, TableProperties,
  Undo, HorizontalLine,
  SpecialCharacters, SpecialCharactersEssentials,
  Highlight,
]

const BASE_CONFIG = {
  licenseKey: 'GPL',
  plugins: PLUGINS,
  toolbar: { items: TOOLBAR, shouldNotGroupWhenFull: true },
  image: {
    toolbar: [
      'imageStyle:inline', 'imageStyle:block', 'imageStyle:side', '|',
      'toggleImageCaption', 'imageTextAlternative', '|',
      'resizeImage',
    ],
  },
  table: {
    contentToolbar: ['tableColumn', 'tableRow', 'mergeTableCells', 'tableProperties', 'tableCellProperties'],
  },
  list: {
    properties: { styles: true, startIndex: true, reversed: true },
  },
  heading: {
    options: [
      { model: 'paragraph', title: 'Paragraph', class: 'ck-heading_paragraph' },
      { model: 'heading2', view: 'h2', title: 'Sarlavha 2', class: 'ck-heading_heading2' },
      { model: 'heading3', view: 'h3', title: 'Sarlavha 3', class: 'ck-heading_heading3' },
    ],
  },
}

const EDITOR_STYLES = `
  .ck.ck-editor {
    --ck-border-radius: 8px;
    --ck-color-base-border: var(--border);
    --ck-color-toolbar-background: var(--bg-soft);
    --ck-color-base-background: var(--surface);
    --ck-color-text: var(--ink);
    --ck-color-button-default-hover-background: var(--bg-soft);
    --ck-color-button-on-background: var(--coral-tint);
    --ck-color-button-on-color: var(--coral);
    --ck-color-button-on-hover-background: var(--coral-tint);
    --ck-color-focus-border: var(--coral);
    --ck-color-shadow-inner: transparent;
    --ck-focus-ring: 2px solid var(--coral);
    font-family: inherit;
  }
  .ck.ck-editor__top .ck-sticky-panel .ck-toolbar {
    border-radius: 8px 8px 0 0 !important;
    border-color: var(--border) !important;
    padding: 2px 4px !important;
  }
  .ck.ck-editor__main > .ck-editor__editable {
    border-radius: 0 0 8px 8px !important;
    border-color: var(--border) !important;
    background: var(--surface) !important;
    color: var(--ink) !important;
    font-size: 13px;
    line-height: 1.6;
  }
  .ck.ck-editor__main > .ck-editor__editable.ck-focused {
    border-color: var(--coral) !important;
    box-shadow: 0 0 0 2px var(--coral-tint) !important;
  }
  .ck-content p { margin: 0.3em 0; }
  .ck-content img { max-width: 100%; border-radius: 6px; }
`

let stylesInjected = false
function injectStyles() {
  if (stylesInjected) return
  stylesInjected = true
  const el = document.createElement('style')
  el.textContent = EDITOR_STYLES
  document.head.appendChild(el)
}

function stripHtml(html) {
  return html?.replace(/<[^>]*>/g, '').trim() || ''
}

export default function RichTextEditor({
  value,
  onChange,
  placeholder = 'Matn kiriting...',
  minHeight = 90,
}) {
  const [active, setActive] = useState(false)
  const editorRef = useRef(null)
  const wrapRef = useRef(null)
  const initialValue = useRef(value)

  useEffect(() => { injectStyles() }, [])

  // Click outside the whole wrapper → deactivate
  useEffect(() => {
    if (!active) return
    function onMouseDown(e) {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setActive(false)
      }
    }
    // Use capture phase and delay to let CKEditor finish mounting first
    const timer = setTimeout(() => {
      document.addEventListener('mousedown', onMouseDown)
    }, 300)
    return () => {
      clearTimeout(timer)
      document.removeEventListener('mousedown', onMouseDown)
    }
  }, [active])

  const hasContent = stripHtml(value).length > 0

  return (
    <div ref={wrapRef}>
      {/* Preview — shown when not active */}
      {!active && (
        <div
          onClick={() => setActive(true)}
          style={{
            minHeight,
            border: '1px solid var(--border)',
            borderRadius: 8,
            padding: '8px 12px',
            background: 'var(--surface)',
            color: hasContent ? 'var(--ink)' : 'var(--ink-3)',
            fontSize: 13,
            lineHeight: 1.6,
            cursor: 'text',
            transition: 'border-color .15s',
          }}
          onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--coral)' }}
          onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)' }}
        >
          {hasContent
            ? <div dangerouslySetInnerHTML={{ __html: sanitizeHtml(value) }} />
            : <span style={{ opacity: 0.45 }}>{placeholder}</span>
          }
        </div>
      )}

      {/* Editor — mounted only when active, key forces fresh instance */}
      {active && (
        <CKEditor
          key="ck-instance"
          editor={ClassicEditor}
          config={{ ...BASE_CONFIG, placeholder }}
          data={initialValue.current || ''}
          onReady={editor => {
            editorRef.current = editor
            // Set current value (might differ from initialValue if edited before)
            if (value !== initialValue.current) {
              editor.setData(value || '')
            }
            // Small delay before focusing so StrictMode unmount/remount doesn't race
            setTimeout(() => {
              try { editor.editing.view.focus() } catch (_) {}
            }, 50)
          }}
          onChange={(_, editor) => {
            onChange(editor.getData())
          }}
          onError={(error, { willEditorRestart }) => {
            if (willEditorRestart) {
              editorRef.current = null
            }
          }}
        />
      )}
    </div>
  )
}
